import { Logger } from "../logger/logger";
import { ILazyFunction } from "../core/lazy";

interface IGetInstance<T> {
	getInstance: ILazyFunction<T>;
}

type ViewModelWithVisiblity = { isVisible: KnockoutObservable<boolean> } | { popupIsVisible: KnockoutObservable<boolean> };

export class KnockoutHelper {
	public static readonly applyBindingsIfElementExists = (viewModel: any, element: Element): boolean => {
		if (element) {
			if (ko.dataFor(element)) {
				Logger.warn(`Knockoutbinding already exists for element ${element.id}`);
				return false;
			} else {
				ko.applyBindings(viewModel, element);
				return true;
			}
		} else {
			Logger.warn(`Knockoutbinding to invalid element (applyBindingsIfElementExists)`);
			return false;
		}
	}

	public static readonly resolveAndApplyBindingsIfElementExists = (viewModelFn: () => any, element: Element): boolean => {
		if (element) {
			let model = viewModelFn();
			if (model) {
				return KnockoutHelper.applyBindingsIfElementExists(model, element);
			} else {
				Logger.warn("No valid model to bind tor element", element);
			}
		}

		return false;
	}

	/**
	 * Applies knockout binding if getInstance was called at least once and the view model becomes visible
	 * @param viewModelClass view model class, which has a static getInstance function
	 * @param elementSelector dom selector
	 */
	public static applyBindingsLazy<TViewModel extends ViewModelWithVisiblity>(viewModelClass: IGetInstance<TViewModel>, elementSelector: string) {
		let applyBinding = (vm: any) => {
			let element = $(elementSelector)[0];
			if (!element) {
				Logger.error("Cannot find element", { vm, elementSelector });
			}

			this.applyBindingsIfElementExists(vm, element);
		};

		let waitUntilVisible = (vm: any) => {
			let isVisible: KnockoutObservable<boolean> = vm.isVisible || vm.popupIsVisible;
			if (isVisible()) {
				applyBinding(vm);
			} else {
				let subscription = isVisible.subscribe((newValue) => {
					if (newValue) {
						subscription.dispose();
						applyBinding(vm);
					}
				});
			}
		};

		if (viewModelClass.getInstance.isValueCreated) {
			waitUntilVisible(viewModelClass.getInstance());
		} else {
			let impl = viewModelClass.getInstance;
			viewModelClass.getInstance = () => {
				let vm = impl();
				waitUntilVisible(vm);
				viewModelClass.getInstance = impl;
				return vm;
			};
		}
	}

	public static readonly property = <TSource extends object, TKey extends keyof TSource>(source: KnockoutObservable<TSource>, key: TKey): KnockoutObservable<TSource[TKey]> => {
		return ko.computed({
			read: () => {
				return source()[key];
			},
			write: (value) => {
				// NOTE(jpg): clone source object so that change to source will be notified
				let clone = $.extend(true, {}, source());
				clone[key] = value;
				source(clone);
			},
		}).extend({ notify: "always" });
	}
}